import React, { useState } from 'react';
import axios from 'axios';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

const DataItem = ({ label, value }) => {
  if (Array.isArray(value) && value.length === 0) {
    return null;
  }
  if (value === null || typeof value === 'undefined' || value === '') {
    return null;
  }

  let displayValue;

  if (Array.isArray(value)) {
    displayValue = value.map(String).join(' • ');
  } else {
    displayValue = String(value);
  }
  const dateLabels = ["Birth Date", "First Seen", "Last Seen", "Last Change"];
  if (dateLabels.includes(label) && typeof displayValue === 'string' && !React.isValidElement(displayValue)) {
    try {
      const dateObj = new Date(displayValue);
      if (!isNaN(dateObj.getTime())) {
        if (displayValue.includes('T')) {
          displayValue = dateObj.toLocaleString();
        } else {
          const year = dateObj.getUTCFullYear();
          const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getUTCDate()).padStart(2, '0');
          displayValue = `${year}-${month}-${day}`;
        }
      }
    } catch (e) { /* ignore, keep original string if parsing fails */ }
  }
  if (label === "Sources" && Array.isArray(value)) {
    displayValue = value.map((item, index) => {
      if (React.isValidElement(item)) {
        return item;
      }
      return (
        <a key={index} href={item} target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: '#007bff', textDecoration: 'underline' }}>
          {item}
        </a>
      );
    });
  } else if (label === "Wikidata ID" && typeof displayValue === 'string' && displayValue.match(/^Q[0-9]+$/) && !React.isValidElement(displayValue)) {
    displayValue = <a href={`https://www.wikidata.org/wiki/${displayValue}`} target="_blank" rel="noreferrer">{displayValue}</a>;
  }

  const countryLabels = ["Country", "Citizenship"];
  if (countryLabels.includes(label) && Array.isArray(value)) {
    displayValue = value.map(code => {
      if (typeof code === 'string' && code.length === 2) {
        const countryMap = {
          'pk': 'Pakistan (pk)',
          'us': 'United States (us)',
          'gb': 'United Kingdom (gb)',
          'in': 'India (in)',
          'cn': 'China (cn)',
          'de': 'Germany (de)',
          'fr': 'France (fr)',
          'jp': 'Japan (jp)',
          'ca': 'Canada (ca)',
          'au': 'Australia (au)'
        };
        return countryMap[code.toLowerCase()] || `${code.toUpperCase()}`;
      }
      return code;
    }).join(' • ');
  }
  return (
    <div className="detail-row">
      <div className={`detail-label font-weight-bold`}>{label}:</div>
      <div className="detail-value">{displayValue}</div>
    </div>
  );
};

const MatchPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [activeCard, setActiveCard] = useState(null); 

  const handleSearch = async () => {
    if (!query) return;
    setLoading(true);
    setSearched(true);
    setResults([]);

    const requestBody = {
      queries: {
        q1: {
          schema: 'Person',
          properties: { name: [query] },
        },
      },
    };

    try {
      const response = await axios.post(
        'https://pep-databasebe.appinsnap.com/api/Match',
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      const matched = response.data.responses?.q1?.results || [];
      setResults(matched);
    } catch (error) {
      console.error('Match API error:', error.response || error.message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleCard = (index) => {
    setActiveCard(activeCard === index ? null : index);
  };

  return (
    <div className="p-4" style={{ backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <div className="flex justify-content-center mb-4">
        <div className="flex" style={{ maxWidth: '600px', width: '100%' }}>
          <InputText
            type="text"
            placeholder="Search for a person..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(event) => {
              if (event.key === 'Enter') handleSearch();
            }}
            className="p-inputtext-lg flex-1"
            style={{ borderRadius: '50px', marginRight: '8px' }}
          />
          <Button
            label={loading ? 'Searching...' : 'Search'}
            icon="pi pi-search"
            className="p-button-primary"
            onClick={handleSearch}
            disabled={loading}
            style={{ borderRadius: '50px' }}
          />
        </div>
      </div>

      {loading && (
        <div className="flex justify-content-center mt-4">
          <p>Loading results...</p>
        </div>
      )}

      {!loading && searched && results.length === 0 && (
        <div className="flex justify-content-center mt-4">
          <p>No results found for "{query}".</p>
        </div>
      )}

      {!loading && results.length > 0 && (
        <div className="grid justify-content-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {results.map((item, index) => (
            <Card
              key={item.id || index}
              className="mb-3"
              style={{ width: '900px', margin: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem', cursor: 'pointer' }} // Added cursor pointer to indicate clickability
              onClick={() => toggleCard(index)} 
            >
              <div className="flex align-items-center justify-content-between mb-2">
                <div className="flex align-items-center">
                  <i className="pi pi-chevron-down" style={{ marginRight: '8px', color: '#333' }}></i>
                  <h5 className="mb-0" style={{ color: '#333' }}>{item.caption || 'N/A'}</h5>
                </div>
                {item.score && (
                  <Tag value={`${(item.score * 100).toFixed(1)}%`} severity="info" />
                )}
              </div>
              {activeCard === index && (
                <div>
                  <DataItem label="Type" value={item.schema} />
                  <DataItem label="Title" value={item.properties?.title?.[0]} />
                  <DataItem label="Birth Date" value={item.properties?.birthDate?.[0]} />
                  <DataItem label="Birth Place" value={item.properties?.birthPlace?.[0]} />
                  <DataItem label="Gender" value={item.properties?.gender?.[0]} />
                  <DataItem label="Religion" value={item.properties?.religion?.[0]} />
                  <DataItem label="Country" value={item.properties?.country} />
                  <DataItem label="Citizenship" value={item.properties?.citizenship} />
                  <DataItem label="Position(s)" value={item.properties?.position} />
                  <DataItem label="Topics" value={item.properties?.topics} />
                  <DataItem label="Notes" value={item.properties?.notes} />
                  <DataItem label="Education" value={item.properties?.education} />
                  <DataItem label="Wikidata ID" value={item.properties?.wikidataId?.[0]} />
                  <DataItem label="Datasets" value={item.datasets} />
                  <DataItem label="Referents" value={item.referents} />
                  <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '1rem', paddingTop: '1rem' }}>
                    <DataItem label="All Names" value={item.properties?.name} />
                    <DataItem label="Aliases" value={item.properties?.alias} />
                    <DataItem label="Weak Aliases" value={item.properties?.weakAlias} />
                  </div>
                  {item.properties?.sourceUrl && (
                    <DataItem
                      label="Sources"
                      value={item.properties.sourceUrl.map((url, idx) => (
                        <a key={idx} href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: '#007bff', textDecoration: 'underline' }}>
                          {url}
                        </a>
                      ))}
                    />
                  )}
                  <DataItem label="First Seen" value={item.first_seen} />
                  <DataItem label="Last Seen" value={item.last_seen} />
                  <DataItem label="Last Change" value={item.last_change} />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MatchPage;
